<template>
	<div id="fieldgroup-RPep" :class="['fieldgroup', formData.caBedroom && 'ca_mode']">
		<div class="row no-gutters justify-content-between fieldgroup-labels">
			<div class="col-1"></div>

			<div class="col-1">
				<div class="field-wrap">
					<label class="color-grey-medium">Number of Apartments</label>
				</div>
			</div>

			<div class="col-2">
				<div class="field-wrap">
					<label class="color-grey-medium btn-help">Occupancy Rate</label>

					<select
						:value="formData.RBR_presets"
						@change="handleRbrPresetChange"
						:disabled="formData.caBedroom"
					>
						<option
							v-for="{ value, label } in rPeopleOpts"
							:key="value"
							:value="value"
						>
							{{label}}
						</option>
					</select>
				</div>
			</div>

			<div class="col-4">
				<div class="field-wrap">
					<label class="color-grey-medium btn-help">
						Peak Gallons per Day per Person
					</label>

					<select
						:value="formData.gpdppptionsBR"
						@change="handleGpdppPresetChange"
						:disabled="formData.CAspec"
					>
						<option
							v-for="{ value, label } in gpdppOpts"
							:key="value"
							:value="value"
						>
							{{label}}
						</option>
					</select>
				</div>
			</div>

			<div class="col-2">
				<button
					type="button"
					@click="handleCaBedroomChange"
					id="toggle-ca_bedroom"
					:class="['js-btn-toggle', formData.caBedroom && 'toggled']"
					:disabled="formData.CAspec"
				>
					<i class="toggle-knob"></i>

					<span class="label">California by Bedroom</span>
				</button>
			</div>
		</div>

		<div
			v-for="{ label, numBRs } in brTypes"
			class="row no-gutters justify-content-between align-items-center fieldgroup-inputs"
			:key="label"
		>
			<div class="col-1">
				<div class="field-wrap">
					<label class="color-grey-medium text-right pt-3">{{label}}</label>
				</div>
			</div>

			<div class="col-1">
				<FormInput
					:dataKey="`nBR${numBRs}`"
					:onInput="debounce(onNBRChange, 500)"
				/>
			</div>

			<div class="col-2">
				<FormInput
					:dataKey="`rBR${numBRs}`"
					:fieldWrapClass="formData.caBedroom && 'locked'"
					:readOnly="formData.caBedroom"
					:onInput="onRBRChange"
				/>
			</div>

			<div class="col-4">
				<div :class="['field-wrap', formData.caBedroom && 'hide']">
					<WaterSlider
						:value="formData[`gBR${numBRs}`]"
						:onUpdate="val => formData[`gBR${numBRs}`] = val"
					/>
				</div>
			</div>

			<div class="col-2"></div>
		</div>

		<div
			id="calculated-gpdpp"
			:class="{
				container: true,
				loading: loadingGpdpp,
				active: caGpdpp !== undefined && !loadingGpdpp
			}"
		>
			<div class="row no-gutters justify-content-between align-items-center">
				<div class="col-1"></div>
				<div class="col-1"></div>
				<div class="col-2"></div>

				<div class="col-4">
					<div class="gpdpp-block">
						<div class="icon-loading">
							<figure><i></i>
								<span class="bold">Calculating</span>
							</figure>
						</div>

						<span class="sum bold">{{caGpdpp}}</span>

						<div class="error-msg">
							<h6 class="color-error">There was a problem getting the value</h6>

							<p class="color-grey text-tiny semi">Try again, or enter your other inputs and Size Your System</p>
						</div>

						<p class="description semi">Peak Gallons per Day per Person calculated based on the expected 98th
							percentile of the specific combination of apartments sizes</p>
					</div>
				</div>

				<div class="col-2"></div>
			</div>
		</div>
	</div>
</template>

<script>
import WaterSlider from '@/components/WaterSlider.vue'
import FormInput from '@/components/FormInput'
import formData from '@/components/Sizer/form-data-store'
import debounce from 'lodash/debounce'
import jsonFetch from '@/utils/json-fetch'
import { brTypes, rPeopleOpts, gpdppOpts, rbrPresets, gpdppPresets } from './config'

export default {
	name: 'RPepInputs',

	components: {
		FormInput,
		WaterSlider,
	},

	data: () => ({
		debounce,
		formData,
		brTypes,
		rPeopleOpts,
		gpdppOpts,
		loadingGpdpp: false,
		caGpdpp: undefined,
	}),

	created () {
		const { RBR_presets, gpdppptionsBR } = formData
		const { setRbrValuesForPreset, setGpdppValuesForPreset } = this

		setRbrValuesForPreset(RBR_presets)
		setGpdppValuesForPreset(gpdppptionsBR)
	},

	watch: {
		'formData.CAspec' (newVal) {
			const { setRbrValuesForPreset } = this

			if (newVal) {
				setRbrValuesForPreset('CA')
			}
		}
	},

	methods: {
		handleRbrPresetChange ({ target: { value: preset } }) {
			const { setRbrValuesForPreset } = this

			formData.RBR_presets = preset
			setRbrValuesForPreset(preset)
		},

		handleGpdppPresetChange ({ target: { value: preset } }) {
			const { setGpdppValuesForPreset } = this

			formData.gpdppptionsBR = preset
			formData.caBedroom = preset === 'ca'
			setGpdppValuesForPreset(preset)
		},

		handleCaBedroomChange () {
			const { setRbrValuesForPreset, getCaGpdpp } = this

			formData.RBR_presets = 'CA'
			formData.caBedroom = !formData.caBedroom
			formData.gpdppptionsBR = formData.caBedroom ? 'ca' : 'user'

			setRbrValuesForPreset('CA')

			if (formData.caBedroom) getCaGpdpp()
		},

		onRBRChange () {
			formData.RBR_presets = 'user'
		},

		onNBRChange () {
			const { getCaGpdpp } = this
			const { caBedroom } = formData

			if (caBedroom) getCaGpdpp()
		},

		async getCaGpdpp () {
			const data = [0, 1, 2, 3, 4, 5].map(n => formData[`nBR${n}`])

			if (data.some(Boolean)) {
				this.loadingGpdpp = true

				const { gpdpp } = await jsonFetch({
					url: '/sizer/cagpdpp/',
					body: { data }
				})

				this.caGpdpp = gpdpp.toFixed(2)
				this.loadingGpdpp = false
			} else {
				this.caGpdpp = undefined
			}
		},

		setRbrValuesForPreset (preset) {
			if (rbrPresets[preset]) {
				rbrPresets[preset].forEach((value, i) => {
					formData[`rBR${i}`] = value
				})
			}
		},

		setGpdppValuesForPreset (preset) {
			if (gpdppPresets[preset]) {
				[0, 1, 2, 3, 4, 5].forEach((nBRs) => {
					formData[`gBR${nBRs}`] = gpdppPresets[preset]
				})
			}
		}
	}
}
</script>
